import { useEffect, useMemo, useState } from 'react';
import { ExportFormatDialog, type ExportFormat } from '@/components/common/ExportFormatDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Database, Download, Mail, Loader2, X, CheckCircle2, AlertCircle, Search } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { toCommaList } from '@/utils/search-list';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';

/**
 * Master Data (MIS) — cut a filtered slice of shipment data and either download it or email it to
 * the merchant it belongs to, with every hand-off logged underneath.
 *
 * The filters mirror the Orders list exactly and are sent to the SAME server-side exporter, so what
 * a merchant receives cannot drift from what the operator saw on screen.
 */

const TABS = [
  { value: '', label: 'All orders' },
  { value: 'not-shipped', label: 'Not Shipped' },
  { value: 'booking-failed', label: 'Booking Failed' },
  { value: 'manifest', label: 'Manifested' },
  { value: 'in-transit', label: 'In Transit' },
  { value: 'ofd', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'ndr', label: 'NDR' },
  { value: 'returned', label: 'RTO / Returned' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all-shipped', label: 'All Shipped' },
];

type LogRow = {
  id: number; action: 'EXPORT' | 'EMAIL'; orderType?: string | null;
  userName?: string | null; userEmail?: string | null;
  merchantName?: string | null; sentToEmail?: string | null; subject?: string | null;
  rowCount: number; fileName?: string | null; fileSizeBytes?: number | null;
  ok: boolean; errorMessage?: string | null; ip?: string | null; createdAt: string;
  filters?: any;
};

export function MasterDataPage() {
  const qc = useQueryClient();
  // '' = both services in one file, which is the default and the normal MIS request.
  const [orderType, setOrderType] = useState<'' | 'B2C' | 'B2B'>('');
  const [tab, setTab] = useState('');
  // One or several merchants (client ask 03-11). The API already takes `merchantIds`.
  const [merchantIds, setMerchantIds] = useState<(string | number)[]>([]);
  const [courierId, setCourierId] = useState<number | ''>('');
  const [deliveryType, setDeliveryType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [awbNo, setAwbNo] = useState('');
  const [lrNo, setLrNo] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [ewayBillNo, setEwayBillNo] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [askFormat, setAskFormat] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-mis'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 1000 } })).data,
  });
  const merchantList = useMemo(
    () => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []),
    [merchants],
  );
  // No orderType filter — with the combined view the courier list must span both services, or a
  // B2B courier would be unselectable while the file itself happily contains B2B rows.
  const { data: couriers } = useQuery({
    queryKey: ['couriers-mis', orderType],
    queryFn: async () => (await api.get('/couriers', { params: { ...(orderType ? { orderType } : {}), isActive: 'true' } })).data,
  });

  /** The single filter object sent to export, email-preview and email — one shape, one meaning. */
  const filters = useMemo(() => {
    const f: any = {};
    if (orderType) f.orderType = orderType;   // omitted ⇒ B2C and B2B together
    if (tab) f.tab = tab;
    if (merchantIds.length) f.merchantIds = merchantIds.map(Number).join(',');
    if (courierId) f.courierId = courierId;
    if (deliveryType) f.deliveryType = deliveryType;
    if (startDate) f.startDate = startDate;
    if (endDate) f.endDate = endDate;
    if (awbNo.trim()) f.awbNo = awbNo.trim();
    if (lrNo.trim()) f.lrNo = lrNo.trim();
    if (orderNo.trim()) f.orderNo = orderNo.trim();
    if (invoiceNo.trim()) f.invoiceNo = invoiceNo.trim();
    if (ewayBillNo.trim()) f.ewayBillNo = ewayBillNo.trim();
    if (customerMobile.trim()) f.customerMobile = customerMobile.trim();
    if (search.trim()) f.search = search.trim();
    return f;
  }, [orderType, tab, merchantIds, courierId, deliveryType, startDate, endDate, awbNo, lrNo, orderNo, invoiceNo, ewayBillNo, customerMobile, search]);

  const qs = useMemo(
    () => new URLSearchParams(Object.entries(filters).map(([k, v]) => [k, String(v)])).toString(),
    [filters],
  );

  /**
   * A booking date range is REQUIRED — mirrors the server, which refuses these routes without one.
   *
   * With the dates blank the export cut every order the platform has ever booked, and that was the
   * easiest thing to press. The carve-out matches the API exactly: a lookup by a specific identifier
   * is already bounded, so it does not need a range.
   */
  const byIdentifier = !!(awbNo.trim() || lrNo.trim() || orderNo.trim() || invoiceNo.trim() || ewayBillNo.trim() || customerMobile.trim());
  const needsDates = !byIdentifier && !(startDate && endDate);
  const badRange = !!startDate && !!endDate && endDate < startDate;
  const dateError = needsDates
    ? 'Pick a booking date range (From and To) before exporting.'
    : badRange ? 'Booking date "To" is before "From".' : '';

  /**
   * How many records the current filters match, refreshed as they change.
   *
   * Shown next to the buttons so the operator knows what they are about to hand over BEFORE they
   * press anything — an empty result used to be discoverable only by opening the downloaded file,
   * or worse, by the merchant receiving a header-only CSV.
   */
  const { data: countData, isFetching: counting } = useQuery({
    queryKey: ['mis-count', filters],
    // Not fired without a valid range: the server would answer 400, and counting "everything" is the
    // exact scan this rule exists to stop.
    enabled: !dateError,
    queryFn: async () => (await api.get('/master-data/count', { params: filters })).data,
  });
  const matchCount: number | undefined = dateError ? undefined : countData?.count;
  const noRecords = matchCount === 0;

  // Opens the Excel-or-CSV question; the download itself runs from `runExport`.
  const doExport = () => {
    if (dateError) { toast.error(dateError); return; }
    if (noRecords) { toast.error('No records found for these filters — nothing to export.'); return; }
    setAskFormat(true);
  };

  const runExport = async (format: ExportFormat) => {
    setExporting(true);
    try {
      await downloadBlob(
        `/master-data/export?${qs}&format=${format}`,
        `master-data-${(orderType || 'all').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.${format}`,
      );
      setAskFormat(false);
      qc.invalidateQueries({ queryKey: ['mis-logs'] });
      toast.success('Export downloaded — recorded in the log below');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Export failed');
    } finally { setExporting(false); }
  };

  const reset = () => {
    setTab(''); setMerchantIds([]); setCourierId(''); setDeliveryType('');
    setStartDate(''); setEndDate(''); setAwbNo(''); setLrNo(''); setOrderNo('');
    setInvoiceNo(''); setEwayBillNo(''); setCustomerMobile(''); setSearch('');
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Database size={22} className="text-indigo-700" /> Master Data
          </h1>
          <p className="text-sm text-slate-500">Cut a filtered data set and hand it to a merchant — as a download, or emailed as an attachment.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* The match count sits WITH the buttons, not buried in the filter panel — it is the thing
              that decides whether pressing either of them makes sense. */}
          <span className={`text-sm px-2.5 py-1 rounded-lg ${
            dateError ? 'bg-amber-50 text-amber-800 font-medium border border-amber-200'
              : counting ? 'text-slate-400'
              : noRecords ? 'bg-rose-50 text-rose-700 font-medium border border-rose-200'
              : 'bg-emerald-50 text-emerald-700 font-medium border border-emerald-200'
          }`}>
            {dateError ? 'Date range required' : counting ? 'Counting…' : noRecords ? 'No records found' : `${matchCount?.toLocaleString('en-IN')} records`}
          </span>
          <button onClick={doExport} disabled={exporting || counting || noRecords || !!dateError}
            title={dateError || (noRecords ? 'Nothing matches these filters' : undefined)}
            className="px-4 py-2 border border-indigo-600 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button onClick={() => setEmailOpen(true)} disabled={counting || noRecords || !!dateError}
            title={dateError || (noRecords ? 'Nothing matches these filters — there is no data to send' : undefined)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            <Mail size={15} /> Send Email
          </button>
        </div>
      </div>

      {/* Filters — same set as the Orders list, so the file matches what ops would see there.
          There is deliberately NO B2C/B2B tab: MIS hands a merchant one file covering everything
          they shipped, and a tab would force two exports that then have to be stapled together.
          Service Type is an ordinary optional filter, blank = both. */}
      {!!dateError && (
        <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          <span>
            <b>{dateError}</b>{' '}
            Exporting with no range cuts every order on the platform, which grows with the data and is
            almost never what was wanted. To pull specific shipments instead, fill in AWB, LR, Order No,
            Invoice No, E-Way Bill or Customer Mobile — those need no date range.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-4 gap-3">
          <Field label="Service Type">
            <select value={orderType} onChange={(e) => { setOrderType(e.target.value as any); setCourierId(''); }} className="input w-full">
              <option value="">B2C + B2B (all)</option>
              <option value="B2C">B2C only</option>
              <option value="B2B">B2B only</option>
            </select>
          </Field>
          <div data-testid="md-merchants">
            <MultiSelectFilter label="Merchant" values={merchantIds} onChange={setMerchantIds}
              options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}` }))}
              placeholder="All merchants" />
          </div>
          <Field label="Status / Tab">
            <select value={tab} onChange={(e) => setTab(e.target.value)} className="input w-full">
              {TABS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Courier">
            <select value={courierId} onChange={(e) => setCourierId(e.target.value ? Number(e.target.value) : '')} className="input w-full">
              <option value="">All couriers</option>
              {(couriers ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Payment Mode">
            <select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)} className="input w-full">
              <option value="">All</option>
              <option value="COD">COD</option>
              <option value="PREPAID">Prepaid</option>
            </select>
          </Field>

          {/* Required — see `dateError` above. Marked with * and outlined in amber when missing, so the
              rule is visible on the field itself and not only as a refusal after pressing Export. */}
          <Field label="Booking Date From *">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className={`input w-full ${needsDates && !startDate ? 'border-amber-400 bg-amber-50' : ''}`} />
          </Field>
          <Field label="Booking Date To *">
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate || undefined}
              className={`input w-full ${(needsDates && !endDate) || badRange ? 'border-amber-400 bg-amber-50' : ''}`} />
          </Field>
          {/* AWB and LR are BOTH always shown. Hiding LR behind the B2B tab meant a B2C order that
              carries an LR could not be looked up at all, and it read as "the filter is missing". */}
          <Field label="AWB No">
            {/* Spaces and line breaks between numbers turn into commas as you type or paste. */}
            <input value={awbNo} onChange={(e) => setAwbNo(toCommaList(e.target.value))} placeholder="One or many — spaces become commas" className="input w-full font-mono" />
          </Field>
          <Field label="LR No">
            <input value={lrNo} onChange={(e) => setLrNo(toCommaList(e.target.value))} placeholder="One or many — spaces become commas" className="input w-full font-mono" />
          </Field>

          <Field label="Order No">
            <input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="One or many, comma separated" className="input w-full" />
          </Field>
          <Field label="Invoice No">
            <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="One or many, comma separated" className="input w-full" />
          </Field>
          <Field label="E-Way Bill No">
            <input value={ewayBillNo} onChange={(e) => setEwayBillNo(e.target.value)} placeholder="One or many, comma separated" className="input w-full font-mono" />
          </Field>

          <Field label="Customer Mobile">
            <input value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value)} placeholder="One or many, comma separated" className="input w-full font-mono" />
          </Field>
          <Field label="Search">
            <div className="flex items-center gap-1 border rounded-lg px-2 py-1.5 bg-white">
              <Search size={14} className="text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Any field…" className="flex-1 text-sm outline-none" />
            </div>
          </Field>
          <div className="col-span-2 flex items-end">
            <button onClick={reset} className="px-3 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50">Reset filters</button>
          </div>
        </div>
      </div>

      {emailOpen && (
        <SendEmailModal
          filters={filters}
          merchantList={merchantList}
          presetMerchantId={merchantIds.length === 1 ? Number(merchantIds[0]) : ''}
          onClose={() => setEmailOpen(false)}
          onSent={() => { setEmailOpen(false); qc.invalidateQueries({ queryKey: ['mis-logs'] }); }}
        />
      )}

      <LogTable />

      <ExportFormatDialog
        open={askFormat}
        title="Download Master Data"
        subtitle={matchCount != null ? `${matchCount.toLocaleString('en-IN')} record(s) — which format?` : 'Which format would you like?'}
        busy={exporting}
        onPick={runExport}
        onClose={() => setAskFormat(false)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-[11px] text-slate-500 mb-1">{label}</div>{children}</div>;
}

/**
 * Pick the merchant, confirm the address, review the composed message, send.
 *
 * The subject/body/address come from the SERVER (`/master-data/email-preview`) rather than being
 * assembled here, so the row count quoted in the body is the real one and the wording stored in the
 * log is the wording that went out.
 */
function SendEmailModal({
  filters, merchantList, presetMerchantId, onClose, onSent,
}: {
  filters: any; merchantList: any[]; presetMerchantId: number | '';
  onClose: () => void; onSent: () => void;
}) {
  const [merchantId, setMerchantId] = useState<number | ''>(presetMerchantId);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [touched, setTouched] = useState(false);

  const { data: preview, isFetching, error } = useQuery({
    queryKey: ['mis-email-preview', merchantId, filters],
    enabled: !!merchantId,
    queryFn: async () => (await api.get('/master-data/email-preview', { params: { ...filters, merchantId } })).data,
  });

  // Fill from the server the first time a merchant is chosen, and on every merchant change — but
  // never overwrite wording the operator has already edited.
  useEffect(() => {
    if (!preview) return;
    setTo(preview.email ?? '');
    if (!touched) { setSubject(preview.subject ?? ''); setBody(preview.body ?? ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.merchantId]);

  const send = useMutation({
    mutationFn: async () =>
      (await api.post('/master-data/email', { merchantId, to, subject, body, filters: { ...filters, merchantId } }, { timeout: 10 * 60_000 })).data,
    onSuccess: (r: any) => {
      toast.success(`Sent ${r.rows} row(s) to ${r.to}`);
      onSent();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send the email'),
  });

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());
  /**
   * The row count is a SEND CONDITION, not just a warning.
   *
   * The panel below has always said "No records found", but the button stayed live — so the operator
   * pressed it, waited, and got a red toast back from the server. Same outcome, worse experience, and
   * it reads as "the mail went out empty". Gate on the count here so an empty cut is unpressable, and
   * treat "preview not loaded yet" as not-sendable rather than assuming rows exist.
   */
  const hasRows = !!preview && preview.rows > 0;
  const canSend = !!merchantId && hasRows && !isFetching && validEmail && !!subject.trim() && !!body.trim() && !send.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-[10001] flex items-start justify-center pt-10 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl w-[680px] max-w-[95vw]" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><Mail size={16} /> Send data to merchant</h3>
          <button onClick={onClose}><X size={18} className="text-slate-400 hover:text-slate-700" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[11px] text-slate-500 mb-1">Merchant *</div>
            <SearchableSelect value={merchantId} onChange={(v) => { setMerchantId(v ? Number(v) : ''); setTouched(false); }}
              options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}` }))}
              placeholder="Choose the merchant this data belongs to" />
            <div className="text-[11px] text-slate-500 mt-1">
              The attachment is cut for <b>this</b> merchant only — whatever merchant filter was set on the screen behind is overridden.
            </div>
          </div>

          {isFetching && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Preparing the message…</div>}
          {/* Show what the SERVER said — a missing booking date range is refused here too, and
              "Could not load this merchant" sent the operator looking in the wrong place. */}
          {!!error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">
              {(error as any)?.response?.data?.message ?? 'Could not load this merchant.'}
            </div>
          )}

          {preview && (
            <>
              {!preview.hasEmailOnFile && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  This merchant has no email address on file — type the recipient below.
                </div>
              )}
              <div>
                <div className="text-[11px] text-slate-500 mb-1">To *</div>
                <input value={to} onChange={(e) => setTo(e.target.value)}
                  className={`input w-full ${to && !validEmail ? 'border-rose-400' : ''}`} placeholder="merchant@example.com" />
                {to && !validEmail && <div className="text-[11px] text-rose-600 mt-1">That is not a valid email address.</div>}
              </div>
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Subject *</div>
                <input value={subject} onChange={(e) => { setSubject(e.target.value); setTouched(true); }} className="input w-full" />
              </div>
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Message *</div>
                <textarea value={body} onChange={(e) => { setBody(e.target.value); setTouched(true); }} rows={9} className="input w-full resize-y" />
              </div>
              <div className={`text-xs rounded-lg px-3 py-2 border ${
                preview.rows === 0 ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-600'
              }`}>
                <div className="font-medium mb-1">
                  {preview.rows === 0
                    ? 'No records found — there is nothing to send'
                    : `Attachment — ${preview.rows.toLocaleString('en-IN')} record(s), cut with:`}
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {(preview.filterSummary ?? []).map((l: string, i: number) => <li key={i}>{l}</li>)}
                </ul>
                {preview.rows === 0 && (
                  <div className="mt-1.5">
                    This merchant has no orders matching these filters. Widen the date range, or check you picked the
                    right merchant — sending now would deliver an empty file.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600">Cancel</button>
          <button onClick={() => send.mutate()} disabled={!canSend}
            title={!merchantId ? 'Choose a merchant first'
              : isFetching ? 'Still counting the records…'
              : !hasRows ? 'No records found for these filters — there is nothing to attach'
              : undefined}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
            {send.isPending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            {send.isPending ? 'Sending…' : !hasRows && !!preview ? 'Nothing to send' : 'Send with attachment'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Who took or sent what, when, and to whom — including the sends that failed. */
function LogTable() {
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['mis-logs', { action, from, to }],
    queryFn: async () => (await api.get('/master-data/logs', { params: { action: action || undefined, from: from || undefined, to: to || undefined, pageSize: 100 } })).data,
  });
  const items: LogRow[] = data?.items ?? [];
  const kb = (n?: number | null) => (n == null ? '—' : n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-slate-900">Export &amp; Email Log</h3>
        <span className="text-xs text-slate-400">{data?.total ?? 0} entries</span>
        <div className="ml-auto flex items-center gap-2">
          <select value={action} onChange={(e) => setAction(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
            <option value="">All actions</option>
            <option value="EXPORT">Exports</option>
            <option value="EMAIL">Emails</option>
          </select>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">When</th>
              <th className="text-left px-3 py-2.5">How</th>
              <th className="text-left px-3 py-2.5">By</th>
              <th className="text-left px-3 py-2.5">Merchant</th>
              <th className="text-left px-3 py-2.5">Sent to</th>
              <th className="text-right px-3 py-2.5">Rows</th>
              <th className="text-left px-3 py-2.5">File</th>
              <th className="text-left px-3 py-2.5">Result</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-8"><Loader2 size={18} className="mx-auto animate-spin text-indigo-600" /></td></tr>}
            {!isLoading && items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">Nothing exported or emailed yet.</td></tr>}
            {items.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(l.createdAt).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${l.action === 'EMAIL' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                    {l.action}
                  </span>
                  {l.orderType && <span className="ml-1 text-[10px] text-slate-400">{l.orderType}</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-slate-800">{l.userName || '—'}</div>
                  <div className="text-slate-400">{l.userEmail}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-700">{l.merchantName || <span className="text-slate-400">All / none</span>}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-mono">{l.sentToEmail || '—'}</div>
                  {l.subject && <div className="text-slate-400 max-w-[240px] truncate" title={l.subject}>{l.subject}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-right font-medium">{l.rowCount}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="max-w-[200px] truncate" title={l.fileName ?? ''}>{l.fileName || '—'}</div>
                  <div className="text-slate-400">{kb(l.fileSizeBytes)}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {l.ok
                    ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> OK</span>
                    : <span className="text-rose-700 inline-flex items-center gap-1" title={l.errorMessage ?? ''}><AlertCircle size={12} /> Failed</span>}
                  {l.ip && <div className="text-slate-400 font-mono">{l.ip}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
